import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../hooks/useLanguage';

function Navbar() {
  const { t } = useTranslation();
  const { changeLanguage, isCurrentLanguage } = useLanguage();

  const NAV_LINKS = [
    { label: t('navigation.home'), href: '/' },
    { 
      label: t('navigation.about'), 
      href: '/about',
      dropdown: [
        { label: t('navigation.gallery'), href: '/innerpages/gallery' }
      ]
    },
    { label: t('navigation.projects'), href: '/innerpages/portfolio' },
    { label: t('navigation.contact'), href: '/innerpages/contact' },
  ];

  const LANGUAGE_SWITCH = [
    { label: t('language.en'), code: 'en' },
    { label: t('language.bg'), code: 'bg' },
    { label: t('language.de'), code: 'de' },
    { label: t('language.ru'), code: 'ru' },
  ];

  return (
    <nav className="navbar navbar-expand-lg navbar-dark tc-navbar-style1 section-padding-x">
      <div className="container-fluid content">
        {/* Mobile Logo */}
        <a className="navbar-brand d-block d-lg-none" href="/">
          <img src="/home1/assets/img/logo_dark.png" alt="Mobile Logo" className="logo" />
        </a>

        <button
          className="navbar-toggler d-block d-lg-none"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarSupportedContent"
          aria-controls="navbarSupportedContent"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className="collapse navbar-collapse" id="navbarSupportedContent">
          {/* Desktop Layout with Centered Logo */}
          <div className="d-none d-lg-flex w-100 align-items-center">
            {/* Left Navigation Links */}
            <div className="nav-left d-flex">
              {NAV_LINKS.slice(0, 2).map((link, index) => (
                link.dropdown ? (
                  <div key={index} className="nav-item dropdown me-4 navbar-dropdown-hover">
                    <a
                      className={`nav-link dropdown-toggle ${window.location.pathname === link.href ? ' active' : ''}`}
                      href={link.href}
                      style={{ padding: '10px 20px', fontWeight: 500, textTransform: 'uppercase', cursor: 'pointer' }}
                    >
                      {link.label}
                    </a>
                    <ul className="dropdown-menu">
                      {link.dropdown.map((item, itemIndex) => (
                        <li key={itemIndex}>
                          <a 
                            className={`dropdown-item ${window.location.pathname === item.href ? ' active' : ''}`}
                            href={item.href}
                            style={{ textTransform: 'uppercase' }}
                          >
                            {item.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <a
                    key={index}
                    className={`nav-link me-4 ${window.location.pathname === link.href ? ' active' : ''}`}
                    href={link.href}
                    style={{ padding: '10px 20px', fontWeight: 500, textTransform: 'uppercase' }}
                  >
                    {link.label}
                  </a>
                )
              ))}
            </div>

            {/* Centered Logo */}
            <div className="nav-center flex-grow-1 d-flex justify-content-center">
              <a className="navbar-brand" href="/">
                <img src="/home1/assets/img/logo_light.png" alt="Logo" className="logo" />
              </a>
            </div>

            {/* Right Navigation Links */}
            <div className="nav-right d-flex align-items-center">
              {NAV_LINKS.slice(2).map((link, index) => (
                <a
                  key={index + 2}
                  className={`nav-link me-4 ${window.location.pathname === link.href ? ' active' : ''}`}
                  href={link.href}
                  style={{ padding: '10px 20px', fontWeight: 500, textTransform: 'uppercase' }}
                >
                  {link.label}
                </a>
              ))}

              {/* Language Switcher */}
              <div className="nav-side d-flex align-items-center">
                {LANGUAGE_SWITCH.map((lang, index) => (
                  <button 
                    type="button"
                    className={`icon ms-2 ${isCurrentLanguage(lang.code) ? 'active' : ''}`} 
                    key={index}
                    onClick={() => {
                      console.log('Changing language to:', lang.code);
                      changeLanguage(lang.code);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: isCurrentLanguage(lang.code) ? '#ffffff' : '#999999',
                      fontSize: '12px',
                      padding: '6px 10px',
                      transition: 'all 0.3s ease',
                      fontWeight: isCurrentLanguage(lang.code) ? 'bold' : 'normal',
                      opacity: 1
                    }}
                  >
                    <span>{lang.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="d-block d-lg-none">
            <ul className="navbar-nav">
              {NAV_LINKS.map((link, index) => {
                // On mobile, show dropdown items as regular menu items
                if (link.dropdown) {
                  return (
                    <React.Fragment key={index}>
                      <li className="nav-item">
                        <a
                          className={`nav-link${window.location.pathname === link.href ? ' active' : ''}`}
                          href={link.href}
                          style={{ padding: '10px 20px', fontWeight: 500 }}
                        >
                          {link.label}
                        </a>
                      </li>
                      {link.dropdown.map((item, itemIndex) => (
                        <li className="nav-item" key={`dropdown-${index}-${itemIndex}`}>
                          <a
                            className={`nav-link${window.location.pathname === item.href ? ' active' : ''}`}
                            href={item.href}
                            style={{ padding: '10px 20px', fontWeight: 500, paddingLeft: '40px' }}
                          >
                            {item.label}
                          </a>
                        </li>
                      ))}
                    </React.Fragment>
                  );
                }
                return (
                  <li className="nav-item" key={index}>
                    <a
                      className={`nav-link${window.location.pathname === link.href ? ' active' : ''}`}
                      href={link.href}
                      style={{ padding: '10px 20px', fontWeight: 500 }}
                    >
                      {link.label}
                    </a>
                  </li>
                );
              })}
            </ul>

            <div className="nav-side d-flex align-items-center justify-content-center mt-3 mb-3">
              {LANGUAGE_SWITCH.map((lang, index) => (
                <button 
                  type="button"
                  className={`icon ${isCurrentLanguage(lang.code) ? 'active' : ''}`} 
                  key={index}
                  onClick={() => {
                    console.log('Changing language to:', lang.code);
                    changeLanguage(lang.code);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: isCurrentLanguage(lang.code) ? '#000000' : '#666666',
                    fontSize: '14px',
                    padding: '8px 12px',
                    margin: '0 5px',
                    transition: 'all 0.3s ease',
                    fontWeight: isCurrentLanguage(lang.code) ? 'bold' : 'normal',
                    opacity: 1
                  }}
                >
                  <span>{lang.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Hover Dropdown Styles */}
      <style>{`
        @media (min-width: 992px) {
          .navbar-dropdown-hover {
            position: relative;
          }

          .navbar-dropdown-hover .dropdown-menu {
            display: none;
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.2s ease, visibility 0.2s ease;
            position: absolute;
            top: 100%;
            left: 0;
            margin: 0;
            padding-top: 5px;
            border: none;
            box-shadow: none;
            background: transparent !important;
            backdrop-filter: none;
            padding-bottom: 8px;
            min-width: auto;
            z-index: 1000;
            pointer-events: none;
          }

          .navbar-dropdown-hover:hover .dropdown-menu {
            display: block;
            opacity: 1;
            visibility: visible;
            pointer-events: auto;
          }

          .navbar-dropdown-hover .dropdown-menu::before {
            content: '';
            position: absolute;
            top: 0;
            left: -20px;
            right: -20px;
            height: 5px;
            background: transparent;
            pointer-events: auto;
          }

          .navbar-dropdown-hover .dropdown-item {
            padding: 8px 20px;
            color: rgba(255, 255, 255, 0.8) !important;
            background: transparent !important;
            transition: color 0.3s ease, opacity 0.3s ease;
            text-decoration: none;
            display: block;
            font-weight: 400;
            letter-spacing: 0.05em;
            white-space: nowrap;
            pointer-events: auto;
            cursor: pointer;
          }

          .navbar-dropdown-hover .dropdown-item:hover {
            background: transparent !important;
            color: rgb(200, 195, 180) !important;
            opacity: 1;
          }

          .navbar-dropdown-hover .dropdown-item.active {
            background: transparent !important;
            color: rgb(200, 195, 180) !important;
            opacity: 1;
            font-weight: 500;
          }

          .navbar-dropdown-hover .dropdown-menu li {
            list-style: none;
          }
        }
        
        /* Mobile: Remove any blue backgrounds and ensure proper styling */
        @media (max-width: 991px) {
          .navbar-nav .dropdown-menu {
            background: transparent !important;
            border: none !important;
            box-shadow: none !important;
          }
          
          .navbar-nav .dropdown-item {
            background: transparent !important;
            color: inherit !important;
          }
          
          .navbar-nav .dropdown-item:hover,
          .navbar-nav .dropdown-item.active,
          .navbar-nav .dropdown-item:focus {
            background: transparent !important;
            background-color: transparent !important;
            color: rgb(200, 195, 180) !important;
          }
          
          .navbar-nav .nav-link.active {
            background: transparent !important;
            background-color: transparent !important;
            color: rgb(200, 195, 180) !important;
          }
          
          .navbar-nav .nav-link:focus {
            background: transparent !important;
            background-color: transparent !important;
          }
        }
        
        /* Global: Remove any Bootstrap blue colors */
        .tc-navbar-style1 .nav-link.active,
        .tc-navbar-style1 .dropdown-item.active {
          background: transparent !important;
          background-color: transparent !important;
        }
        
        .tc-navbar-style1 .nav-link:focus,
        .tc-navbar-style1 .dropdown-item:focus {
          background: transparent !important;
          background-color: transparent !important;
          box-shadow: none !important;
        }
      `}</style>
    </nav>
  );
}

export default Navbar;
